import { erpApi } from '@/shared/axios/erp-api';
import {
  getUrlParamsFetch,
  notFoundIdHandler,
  urlBackend,
} from '@/shared';
import {
  ServicioInternet,
  ServicioInternetPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchServicioInternet = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetServicioInternetParams> = {}) => {
  return useQuery({
    queryKey: ['servicio-internet', ...Object.values(params ?? {})],
    queryFn: () => getServiciosInternet(params),
    enabled: enabled,
  });
};

export const useGetServicioInternet = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['servicio-internet', id],
    queryFn: () => getServicioInternet(id),
    enabled,
  });
};

export const useCreateServicioInternet = ({
  navigate,
  returnUrl,
  returnErrorUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createServicioInternet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicio-internet'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
    },
    onError: () => {
      enableRedirect && navigate && returnErrorUrl && navigate(returnErrorUrl);
    },
  });
};

export const useUpdateServicioInternet = ({
  navigate,
  returnUrl,
  returnErrorUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateServicioInternet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicio-internet'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
    },
    onError: () => {
      enableRedirect && navigate && returnErrorUrl && navigate(returnErrorUrl);
    },
  });
};

export const useDeleteServicioInternet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteServicioInternet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicio-internet'] });
    },
    onError: () => {},
  });
};

///* axios
export type GetServicioInternetParams = Partial<ServicioInternet> & {
  page?: number;
  page_size?: number;
};
export type CreateServicioInternetParams = Omit<
  ServicioInternet,
  'id_servicio_internet'
>;
export type UpdateServicioInternetParams = {
  id: string;
  data: CreateServicioInternetParams;
};

export const getServiciosInternet = async (
  params?: GetServicioInternetParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  return await get<ServicioInternetPagingRes>(
    `${urlBackend}api/v1/serviciointernet/?${queryParams}`,
    true
  );
};

export const getServicioInternet = async (id: string) => {
  const res = await get<ServicioInternet>(
    `${urlBackend}api/v1/serviciointernet/${id}/`,
    true
  );
  notFoundIdHandler(res);
  return res;
};

export const createServicioInternet = async (
  data: CreateServicioInternetParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<ServicioInternet>(
    `${urlBackend}api/v1/serviciointernet/`,
    data,
    true
  );
  setAlert(true, res.message, res.status);
  return res;
};

export const updateServicioInternet = async ({
  id,
  data,
}: UpdateServicioInternetParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<ServicioInternet>(
    `${urlBackend}api/v1/serviciointernet/${id}/`,
    data,
    true
  );
  setAlert(true, res.message, res.status);
  return res;
};

export const deleteServicioInternet = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<ServicioInternet>(
    `${urlBackend}api/v1/serviciointernet/${id}/`,
    true
  );
  setAlert(true, res.message, res.status);
  return res;
};
